import { useRef } from "react";
import { useApp } from "@/context/AppContext";
import { fmtDate, fmtNum } from "@/lib/format";
import { ReportActions } from "@/components/tpv/ZReport";
import { FileImage } from "@/components/files/FileFields";
import { ReportFooter, RTh, RTd } from "@/components/clinic/ReportFrame";
import { attendance, clockStats, monthSummary, payrollCode, payrollTotals, seniority, ym } from "@/lib/hr";

const Box = ({ children, className = "", testid }) => <div className={`border border-[#9A9A9A] rounded-sm p-3 leading-5 ${className}`} data-testid={testid}>{children}</div>;
const Row = ({ label, v, neg, bold, testid }) => <tr className={bold ? "font-bold" : ""}><RTd>{label}</RTd><RTd end dir="ltr"><span data-testid={testid}>{neg && v ? "- " : ""}{fmtNum(v)}</span></RTd></tr>;

export default function PayslipDoc({ member, events, month, breakdown, status = "pending", paidAt }) {
  const { t, view, lang, money } = useApp();
  const ref = useRef(null);
  const shop = view.shop || {};
  const s = monthSummary(member, events, month);
  const b = breakdown || s.payroll?.breakdown || { base: s.base, incentives: s.bonuses, deductions: s.deductions, debt_deduct: s.debt, other: 0 };
  const tot = payrollTotals(b);
  const code = status === "paid" && s.payroll?.code ? s.payroll.code : payrollCode(member, month, tot.total);
  const m = events.filter((e) => ym(e.date) === month);
  const bonuses = m.filter((e) => e.kind === "bonus"), deductions = m.filter((e) => e.kind === "deduction");
  const clock = clockStats(view.attendance, member.id, month), att = attendance(view.shifts || {}, member.name, month);
  const days = clock.days || att.days;
  const owner = view.staff.find((x) => x.role === "owner") || {};
  const sen = seniority(member.hire_date);
  const rate = Number(member.salary || 0);
  const issued = new Date().toISOString();
  const periodLabel = new Date(`${month}-01T00:00:00`).toLocaleDateString(lang === "ar" ? "ar-DZ" : lang === "fr" ? "fr-FR" : "es-ES", { month: "long", year: "numeric" });
  return (
    <div className="space-y-3" data-testid="payslip-doc-wrap">
      <div ref={ref} className="print-area bg-white rounded-xl border border-border p-7 text-[12px] text-[#222] pdf-page flex flex-col" data-testid="payslip-doc">
        {shop.logo_id && <div className="mb-3"><FileImage id={shop.logo_id} className="h-14 max-w-[180px] object-contain object-left" alt={shop.name} /></div>}
        <div className="grid grid-cols-[1.1fr_0.9fr_1.2fr] gap-4 items-start">
          <div className="leading-5" data-testid="payslip-issuer">
            <div className="font-extrabold text-sm uppercase">{shop.name}</div>
            {shop.tax_id && <div dir="ltr">{shop.tax_id}</div>}
            {shop.address && <div>{shop.address}</div>}
            {shop.phone && <div dir="ltr">{shop.phone}</div>}
          </div>
          <Box className="text-center" testid="payslip-title-box">
            <div className="font-extrabold text-base tracking-wide uppercase">{t("hr_payslip")}</div>
            <div className="font-bold mt-1 capitalize" data-testid="payslip-period">{periodLabel}</div>
            <div className="text-[11px] text-[#555]">{t("issue_date")}: <span dir="ltr" data-testid="payslip-issued">{fmtDate(issued, lang)}</span></div>
            <div className={`mt-1 inline-block rounded-sm px-2 py-0.5 text-[11px] font-bold border ${status === "paid" ? "border-[#14532D] text-[#14532D]" : "border-[#9A3412] text-[#9A3412]"}`} data-testid="payslip-status">{status === "paid" ? `${t("hr_status_paid")}${paidAt ? ` · ${paidAt}` : ""}` : t("hr_status_pending")}</div>
          </Box>
          <Box testid="payslip-employee-box">
            <div className="font-bold uppercase">{member.name}</div>
            <div>{t(`role_${member.role}`)}{member.specialty && ` · ${t(`sp_${member.specialty}`)}`}</div>
            {member.id_number && <div>{t("hr_id_number")}: <span dir="ltr">{member.id_number}</span></div>}
            {member.hire_date && <div>{t("hr_hire_date")}: <span dir="ltr">{fmtDate(member.hire_date, lang, false)}</span>{sen && ` · ${sen.years != null ? `${sen.years} ${t("years_short")}` : `${sen.months} ${t("months_short")}`}`}</div>}
            {member.phone && <div dir="ltr">{member.phone}</div>}
          </Box>
        </div>

        <div className="mt-5 pdf-block" data-testid="payslip-contract">
          <div className="font-bold mb-1">{t("employment_details")}</div>
          <table className="w-full">
            <thead><tr><RTh>{t("hr_salary_type")}</RTh><RTh end>{t("hr_rate")}</RTh><RTh end>{t("days_worked")}</RTh><RTh end>{t("hours_worked")}</RTh><RTh end>{t("hr_days_off_month")}</RTh></tr></thead>
            <tbody><tr><RTd>{t(`hr_${member.salary_type || "monthly"}`)}</RTd><RTd end dir="ltr">{fmtNum(rate)}</RTd><RTd end dir="ltr" data-testid="payslip-days">{days || "—"}</RTd><RTd end dir="ltr">{clock.hours ? fmtNum(clock.hours) : att.hours ? fmtNum(att.hours) : "—"}</RTd><RTd end dir="ltr">{member.days_off_month ?? "—"}</RTd></tr></tbody>
          </table>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-5 pdf-block">
          <div data-testid="payslip-earnings">
            <div className="font-bold mb-1">{t("earnings")}</div>
            <table className="w-full">
              <thead><tr><RTh>{t("concept")}</RTh><RTh end>{t("amount")}</RTh></tr></thead>
              <tbody>
                <Row label={`${t("hr_base")} (${t(`hr_${member.salary_type || "monthly"}`)})`} v={Number(b.base) || 0} testid="payslip-base" />
                {Number(b.incentives) > 0 && <Row label={`${t("hr_incentives")}${bonuses.length ? ` · ${bonuses.map((e) => e.note || t("hr_incentive")).join(", ")}` : ""}`} v={Number(b.incentives)} />}
                {Number(b.other) > 0 && <Row label={t("hr_other_earnings")} v={Number(b.other)} />}
                <Row label={t("total_earnings")} v={tot.gross} bold testid="payslip-gross" />
              </tbody>
            </table>
          </div>
          <div data-testid="payslip-deductions">
            <div className="font-bold mb-1">{t("hr_deductions")}</div>
            <table className="w-full">
              <thead><tr><RTh>{t("concept")}</RTh><RTh end>{t("amount")}</RTh></tr></thead>
              <tbody>
                {Number(b.deductions) > 0 && <Row label={`${t("hr_deductions")}${deductions.length ? ` · ${deductions.map((e) => e.note || t("hr_kind_deduction")).join(", ")}` : ""}`} v={Number(b.deductions)} neg />}
                {Number(b.debt_deduct) > 0 && <Row label={`${t("hr_debt_deduct")} (${t("hr_advances")})`} v={Number(b.debt_deduct)} neg />}
                {!(Number(b.deductions) > 0) && !(Number(b.debt_deduct) > 0) && <tr><RTd>—</RTd><RTd /></tr>}
                <Row label={t("total_deductions")} v={tot.deductions} bold neg />
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 flex justify-end pdf-block">
          <table className="min-w-[280px]" data-testid="payslip-totals">
            <tbody>
              <tr><td className="py-1 pe-6 font-extrabold">{t("hr_total_to_pay")}</td><td className="py-1 text-end font-extrabold text-base" dir="ltr" data-testid="payslip-net">{money(tot.total)}</td></tr>
              {tot.shortfall > 0 && <tr><td className="py-1 pe-6 text-[#C7353B]">{t("hr_shortfall")}</td><td className="py-1 text-end text-[#C7353B]" dir="ltr">{money(tot.shortfall)}</td></tr>}
              {b.note && <tr><td colSpan={2} className="py-1 text-[11px] text-[#555]">{b.note}</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="pdf-foot mt-auto">
          <div className="mt-8 grid grid-cols-2 gap-8 items-end text-[12px]" data-testid="payslip-signatures">
            <div className="text-center">
              {member.signature_id ? <div className="h-16 flex items-end justify-center"><FileImage id={member.signature_id} alt="" className="max-h-16 object-contain" /></div> : <div className="h-16" />}
              <div className="border-t border-[#333] pt-1 font-bold">{member.name}</div>
              <div className="text-[#555]">{t("hr_signature")} {t("hr_employee")}</div>
            </div>
            <div className="flex items-end justify-end gap-4">
              {owner.stamp_id && <div className="h-28 w-28 grid place-items-center" data-testid="payslip-stamp"><FileImage id={owner.stamp_id} alt="" className="max-h-28 max-w-28 object-contain" /></div>}
              <div className="text-center w-56">
                {owner.signature_id ? <div className="h-16 flex items-end justify-center" data-testid="payslip-owner-signature"><FileImage id={owner.signature_id} alt="" className="max-h-16 object-contain" /></div> : <div className="h-16" />}
                <div className="border-t border-[#333] pt-1 font-bold">{owner.name || shop.name}</div>
                <div className="text-[#555]">{t("hr_signature")} {t("hr_employer")}</div>
              </div>
            </div>
          </div>
          <div className="mt-5 border-t border-[#DDD] pt-2 text-[8.5px] leading-[11px] text-[#555] text-center">
            <div className="font-semibold">{[shop.name, shop.address, shop.tax_id, shop.phone].filter(Boolean).join(" · ")}</div>
            <div>{t("payslip_legal")}</div>
            <div className="mt-1 font-mono text-[10px] text-[#222] tracking-wider" dir="ltr" data-testid="payslip-code">{t("payslip_code")}: {code}</div>
          </div>
        </div>
      </div>
      <ReportActions target={ref} name={`nomina-${member.name}-${month}`} wide brand={false} />
    </div>
  );
}
